import {
  FormFieldAbstract,
  FormFieldAcceleratedPublication,
  FormFieldAdditionalSupplementary,
  FormFieldAITools,
  FormFieldAnonymizedManuscript,
  FormFieldARRIVEFiles,
  FormFieldArticleSubtype,
  FormFieldAuthors,
  FormFieldCheckbox,
  FormFieldCheckboxArray,
  FormFieldClinicalTrials,
  FormFieldConflictOfInterestForAuthor,
  FormFieldCONSORTFiles,
  FormFieldContactAuthor,
  FormFieldCorrespondingAuthor,
  FormFieldCoverLetter,
  FormFieldDataAvailability,
  FormFieldDateInput,
  FormFieldDefinition,
  FormFieldDoubleAnonymousPeerReview,
  FormFieldEthicsStatement,
  FormFieldGraphicalAbstractImage,
  FormFieldFiguresAndTables,
  FormFieldFiguresInput,
  FormFieldFileUploadArray,
  FormFieldFunding,
  FormFieldFundingGeneric,
  FormFieldIntegerInput,
  FormFieldIPEMMembership,
  FormFieldKeywords,
  FormFieldManuscript,
  FormFieldNoveltyStatement,
  FormFieldOpposedReviewers,
  FormFieldPreferenceImmediatePublication,
  FormFieldPreprint,
  FormFieldPreviouslySubmitted,
  FormFieldPublishingModel,
  FormFieldRadio,
  FormFieldRichTextInput,
  FormFieldSelect,
  FormFieldSourceFiles,
  FormFieldStandardReportingGuidelines,
  FormFieldStandardReportingGuidelinesDetails,
  FormFieldStringInputArray,
  FormFieldSubmittingAuthor,
  FormFieldSubtitle,
  FormFieldSupplementary,
  FormFieldTablesInput,
  FormFieldTextInput,
  FormFieldTitle,
  FormFieldTitlePage,
  FormFieldTransparentPeerReview,
  FormFieldSharedDataset,
  FormFieldGeneratedDataset,
  FormFieldUtilizedDataset,
  FormFieldFocusSelect,
  FormFieldEthicalApproval,
  FormFieldEthicsApproval,
  FormFieldCommentForEditor
} from '@publishing-engine/domain/types/article';
import { useEffect, useState } from 'react';
import { useFormContext } from '../contexts/FormContext';
import { useSubmissionContextData } from '../contexts/SubmissionContextProvider';
import { getFieldConfig } from '../pages/Organization/public/Journal/SubmissionUpload/question-components/utils';
import DraftSubmissionStep from '../pages/Organization/public/Journal/SubmissionUpload/DraftSubmissionStep';
import UploadStep from '../pages/Organization/public/Journal/SubmissionUpload/UploadStep';
import MetadataExtractionStep from '../pages/Organization/public/Journal/SubmissionUpload/MetadataExtractionStep';
import DetailsStep from '../pages/Organization/public/Journal/SubmissionUpload/DetailsStep';
import AuthorStep from '../pages/Organization/public/Journal/SubmissionUpload/AuthorStep';
import PreferenceStep from '../pages/Organization/public/Journal/SubmissionUpload/PreferenceStep';
import FiguresAndTablesStep from '../pages/Organization/public/Journal/SubmissionUpload/FiguresAndTablesStep';
import PreviewStep from '../pages/Organization/public/Journal/SubmissionUpload/PreviewStep';
import SuccessStep from '../pages/Organization/public/Journal/SubmissionUpload/SuccessStep';
import { ExtractType } from '@publishing-engine/domain/types';

export type IUploadStepConfig = {
  Manuscript: FormFieldManuscript;
  TitlePage: FormFieldTitlePage | null;
  ArticleLanguage?: FormFieldTextInput<'metadata.articleLanguage'> | null;
  ArticleSubtype: FormFieldArticleSubtype | null;
};

export type IMetadataExtractionStepConfig = {
  Manuscript: FormFieldManuscript;
  TitlePage: FormFieldTitlePage | null;
  SubmittingAuthor: FormFieldSubmittingAuthor;
  CorrespondingAuthor: FormFieldCorrespondingAuthor | null;
  ContactAuthor: FormFieldContactAuthor | null;
  Authors: FormFieldAuthors | null;
};

export type IDetailsStepConfig = {
  Manuscript: FormFieldManuscript;
  TitlePage: FormFieldTitlePage | null;
  Title: FormFieldTitle;
  Abstract: FormFieldAbstract;
  EnglishAbstract: FormFieldRichTextInput;
  EnglishTitle: FormFieldRichTextInput;
  Keywords: FormFieldKeywords | null;
  SubmissionGuidelines: FormFieldCheckbox<'preference.submissionGuidelines'> | null;
  EnglishKeywords: FormFieldStringInputArray | null;
  SupplementaryUpload: FormFieldSupplementary | null;
  AdditionalSupplementary: FormFieldAdditionalSupplementary | null;
  SourceFiles: FormFieldSourceFiles | null;
  Category: FormFieldSelect<'preference.category'> | null;
  CategoryOther: FormFieldTextInput<'preference.otherCategory'> | null;
  StandardReportingGuidelines: FormFieldStandardReportingGuidelines | null;
  StandardReportingGuidelinesDetails: FormFieldStandardReportingGuidelinesDetails | null;
  StandardReportingGuidelinesFileDetails: FormFieldFileUploadArray<'fileSet.StandardReportingGuidelinesFileDetails'> | null;
  CONSORTFiles: FormFieldCONSORTFiles | null;
  ARRIVEFiles: FormFieldARRIVEFiles | null;
  CoverLetter: FormFieldCoverLetter | null;
  Figures: FormFieldFiguresInput | null;
  GraphicalAbstractImage: FormFieldGraphicalAbstractImage | null;
  NumberOfFigures?: FormFieldIntegerInput | null;
  NumberOfColorFigures?: FormFieldIntegerInput | null;
  NumberOfTables?: FormFieldIntegerInput | null;
  NumberOfWords?: FormFieldIntegerInput | null;
  Tables: FormFieldTablesInput | null;
  Subtitle?: FormFieldSubtitle | null;
  EnglishSubtitle?: FormFieldRichTextInput | null;
  GeneralClassifications?: FormFieldSelect<'preference.generalClassifications'> | null;
  StandardReportingGuidelinesText?: FormFieldTextInput<'preference.standardReportingGuidelinesText'> | null;
  FocusIssue: FormFieldFocusSelect<'preference.focusIssue'> | null;
};

export type IAuthorStepConfig = {
  SubmittingAuthor: FormFieldSubmittingAuthor;
  CorrespondingAuthor: FormFieldCorrespondingAuthor | null;
  ContactAuthor: FormFieldContactAuthor | null;
  Authors: FormFieldAuthors | null;
  CoAuthorsAgreeToEthics: FormFieldCheckbox<'metadata.coauthorsAgreeToEthics'> | null;
  CoAuthorAgreeToSharing: FormFieldCheckbox<'metadata.coauthorsAgreeToSharing'> | null;
  CoAuthorManuscriptOriginalityConfirmed: FormFieldCheckbox<'metadata.coauthorManuscriptOriginalityConfirmed'> | null;
  CoAuthorManuscriptNotUnderConsiderationElsewhere: FormFieldCheckbox<'metadata.coauthorManuscriptNotUnderConsiderationElsewhere'> | null;
  CoAuthorAuthorshipAndConflictDeclaration: FormFieldCheckbox<'metadata.coauthorAuthorshipAndConflictDeclaration'> | null;
  CoAuthorResearchEthicsComplianceConfirmed: FormFieldCheckbox<'metadata.coauthorResearchEthicsComplianceConfirmed'> | null;
  CoAuthorExportControlComplianceConfirmed: FormFieldCheckbox<'metadata.coauthorExportControlComplianceConfirmed'> | null;
};

export type IPreferenceStepConfig = {
  PublishingModel: FormFieldPublishingModel;
  OpenAccessChoice: FormFieldRadio<'preference.openAccessChoice'> | null;
  OpenAccessChoiceWorldBankACountry: FormFieldTextInput<'preference.openAccessChoiceWorldBankACountry'> | null;
  OpenAccessChoiceWorldBankBCountry: FormFieldTextInput<'preference.openAccessChoiceWorldBankBCountry'> | null;
  OpenAccessChoiceReviewerDiscountRate: FormFieldTextInput<'preference.openAccessChoiceReviewerDiscountRate'> | null;
  DataAvailability: FormFieldDataAvailability | null;
  DataAvailabilityOpenlyAvailableUrl: FormFieldTextInput<'preference.dataAvailabilityOpenlyAvailableUrl'> | null;
  DataAvailabilityEmbargoUrl: FormFieldTextInput<'preference.dataAvailabilityEmbargoUrl'> | null;
  DataAvailabilityEmbargoDate: FormFieldDateInput<'preference.dataAvailabilityEmbargoDate'> | null;
  DataUnavailabilityReason: FormFieldRadio<'preference.dataUnavailabilityReason'> | null;
  DataAvailabilityStatement: FormFieldCheckboxArray<'preference.dataAvailabilityStatement'> | null;
  PreferenceImmediatePublication: FormFieldPreferenceImmediatePublication | null;
  DoubleAnonymousPeerReview: FormFieldDoubleAnonymousPeerReview | null;
  AnonymizedManuscript: FormFieldAnonymizedManuscript | null;
  TransparentPeerReview: FormFieldTransparentPeerReview | null;
  NoveltyStatement: FormFieldNoveltyStatement | null;
  FiguresAndTables: FormFieldFiguresAndTables | null;
  LicensesAndPermissions: FormFieldRadio<'metadata.LicensesAndPermissions'> | null;
  LicensesAndPermissionsReproducedContent: FormFieldTextInput<'metadata.LicensesAndPermissionsReproducedContent'> | null;
  LicensesAndPermissionsFiles: FormFieldFileUploadArray<'fileSet.LicensesAndPermissionsFiles'> | null;
  RecommendedReviewers: ExtractType<
    FormFieldDefinition,
    'RecommendedReviewers'
  > | null;
  OpposedReviewers: FormFieldOpposedReviewers | null;
  EthicsStatement: FormFieldEthicsStatement | null;
  ClinicalTrials: FormFieldClinicalTrials | null;
  Funding: FormFieldFunding | null;
  FundingGeneric: FormFieldFundingGeneric | null;
  AcceleratedPublication: FormFieldAcceleratedPublication | null;
  IPEMMembership: FormFieldIPEMMembership | null;
  SubjectArea: FormFieldSelect<'preference.subjectArea'> | null;
  ManuscriptOwnership: FormFieldCheckbox<'metadata.manuscriptOwnership'> | null;
  OpenAccessPublishingConsent: FormFieldCheckbox<'metadata.openAccessPublishingConsent'> | null;
  BlindedReviewConsent: FormFieldCheckbox<'preference.doubleAnonymousPeerReview'> | null; // WARN Conflicts with DoubleAnonymousPeerReview value
  ApcAgreement: FormFieldCheckbox<'metadata.apcAgreement'> | null;
  AuthorDeclaration: FormFieldCheckbox<'metadata.authorDeclaration'> | null;
  BlindedPeerReviewConsent: FormFieldCheckbox<'preference.blindedPeerReviewConsent'> | null;
  PreviouslySubmitted: FormFieldPreviouslySubmitted | null;
  ConflictOfInterestForAuthor: FormFieldConflictOfInterestForAuthor | null;
  AITools: FormFieldAITools | null;
  Preprint: FormFieldPreprint | null;
  OpenAccessOnly: FormFieldCheckbox<'preference.openAccessOnly'> | null;
  EthicsChecklist: FormFieldCheckbox<'metadata.ethicsChecklist'> | null;
  SharedDataset: FormFieldSharedDataset | null;
  GeneratedDataset: FormFieldGeneratedDataset | null;
  UtilizedDataset: FormFieldUtilizedDataset | null;
  OverlappingMaterial: FormFieldCheckbox<'metadata.overlappingMaterial'> | null;
  EthicalApproval: FormFieldEthicalApproval | null;
  EthicsApproval: FormFieldEthicsApproval | null;
  CopyrightLicensingForm: FormFieldCheckbox<'preference.copyrightLicensingForm'> | null;
  CoAuthorApproval: FormFieldRadio<'preference.coAuthorApproval'> | null;
  CommentForEditor: FormFieldCommentForEditor | null;
  DataAvailabilityText: FormFieldTextInput<'preference.dataAvailabilityText'> | null;
};

export type IFiguresAndTablesStepConfig = {
  Figures: FormFieldFiguresInput | null;
  Tables: FormFieldTablesInput | null;
};

export type IPreviewStepConfig = IUploadStepConfig &
  IDetailsStepConfig &
  IAuthorStepConfig &
  IPreferenceStepConfig &
  IFiguresAndTablesStepConfig;

export type SubmissionStepKey =
  | 'draft'
  | 'upload'
  | 'metadataExtraction'
  | 'details'
  | 'author'
  | 'preference'
  | 'figuresAndTables'
  | 'preview'
  | 'success';

export type IStepConfig =
  | {
      stepKey: SubmissionStepKey;
      Component: typeof DraftSubmissionStep;
      configs: null;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof UploadStep;
      configs: null;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof MetadataExtractionStep;
      configs: IMetadataExtractionStepConfig;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof DetailsStep;
      configs: IDetailsStepConfig;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof AuthorStep;
      configs: IAuthorStepConfig;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof PreferenceStep;
      configs: IPreferenceStepConfig;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof FiguresAndTablesStep;
      configs: IFiguresAndTablesStepConfig;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof PreviewStep;
      configs: IPreviewStepConfig;
      displayName: string | null;
    }
  | {
      stepKey: SubmissionStepKey;
      Component: typeof SuccessStep;
      configs: null;
      displayName: string | null;
    };

const isNotEmpty = (configs: Record<string, unknown>) =>
  Object.values(configs).some(Boolean);

export const useSubmissionStepsConfig = () => {
  const { submissionContext } = useSubmissionContextData();
  const {
    formData: {
      submissionContext: {
        schema: { formFields = [] }
      }
    }
  } = useFormContext();

  const authorSubmissionForm =
    submissionContext?.config?.cms?.authorSubmissionForm;

  const detailsStepDisplayName =
    authorSubmissionForm?.reviewSubmissionInformation?.header?.trim() ||
    'Submission Information';
  const authorStepDisplayName =
    authorSubmissionForm?.reviewAuthorInformation?.header?.trim() ||
    'Author Information';
  const preferenceStepDisplayName =
    authorSubmissionForm?.submissionPreferences?.header?.trim() ||
    'Submission Preferences';

  const [steps, setSteps] = useState<IStepConfig[]>([]);

  useEffect(() => {
    const metadataExtractionStepConfigs: IMetadataExtractionStepConfig = {
      Manuscript: getFieldConfig(formFields, 'Manuscript')!,
      TitlePage: getFieldConfig(formFields, 'TitlePage'),
      SubmittingAuthor: getFieldConfig(formFields, 'SubmittingAuthor')!,
      CorrespondingAuthor: getFieldConfig(formFields, 'CorrespondingAuthor'),
      ContactAuthor: getFieldConfig(formFields, 'ContactAuthor'),
      Authors: getFieldConfig(formFields, 'Authors')
    };

    const detailStepConfigs: IDetailsStepConfig = {
      Manuscript: getFieldConfig(formFields, 'Manuscript')!,
      TitlePage: getFieldConfig(formFields, 'TitlePage'),
      FocusIssue: getFieldConfig(formFields, 'FocusIssue'),
      Title: getFieldConfig(formFields, 'Title')!,
      EnglishTitle: getFieldConfig(formFields, 'EnglishTitle')!,
      Subtitle: getFieldConfig(formFields, 'Subtitle'),
      EnglishSubtitle: getFieldConfig(formFields, 'EnglishSubtitle'),
      Abstract: getFieldConfig(formFields, 'Abstract')!,
      EnglishAbstract: getFieldConfig(formFields, 'EnglishAbstract')!,
      Keywords: getFieldConfig(formFields, 'Keywords'),
      EnglishKeywords: getFieldConfig(formFields, 'EnglishKeywords'),
      SubmissionGuidelines: getFieldConfig(formFields, 'SubmissionGuidelines'),
      Category: getFieldConfig(formFields, 'Category'),
      CategoryOther: getFieldConfig(formFields, 'CategoryOther'),
      StandardReportingGuidelines: getFieldConfig(
        formFields,
        'StandardReportingGuidelines'
      ),
      StandardReportingGuidelinesDetails: getFieldConfig(
        formFields,
        'StandardReportingGuidelinesDetails'
      ),
      StandardReportingGuidelinesFileDetails: getFieldConfig(
        formFields,
        'StandardReportingGuidelinesFileDetails'
      ),
      StandardReportingGuidelinesText: getFieldConfig(
        formFields,
        'StandardReportingGuidelinesText'
      ),
      SupplementaryUpload: getFieldConfig(formFields, 'Supplementary'),
      AdditionalSupplementary: getFieldConfig(
        formFields,
        'AdditionalSupplementary'
      ),
      SourceFiles: getFieldConfig(formFields, 'SourceFiles'),

      CONSORTFiles: getFieldConfig(formFields, 'CONSORTFiles'),
      ARRIVEFiles: getFieldConfig(formFields, 'ARRIVEFiles'),
      CoverLetter: getFieldConfig(formFields, 'CoverLetter'),
      Figures: getFieldConfig(formFields, 'Figures'),
      GraphicalAbstractImage: getFieldConfig(
        formFields,
        'GraphicalAbstractImage'
      ),
      Tables: getFieldConfig(formFields, 'Tables'),
      NumberOfFigures: getFieldConfig(formFields, 'NumberOfFigures'),
      NumberOfColorFigures: getFieldConfig(formFields, 'NumberOfColorFigures'),
      NumberOfTables: getFieldConfig(formFields, 'NumberOfTables'),
      NumberOfWords: getFieldConfig(formFields, 'NumberOfWords'),
      GeneralClassifications: getFieldConfig(
        formFields,
        'GeneralClassifications'
      )
    };

    const authorStepConfigs: IAuthorStepConfig = {
      SubmittingAuthor: getFieldConfig(formFields, 'SubmittingAuthor')!,
      ContactAuthor: getFieldConfig(formFields, 'ContactAuthor'),
      CorrespondingAuthor: getFieldConfig(formFields, 'CorrespondingAuthor')!,
      Authors: getFieldConfig(formFields, 'Authors'),
      CoAuthorAgreeToSharing: getFieldConfig(
        formFields,
        'CoAuthorAgreeToSharing'
      ),
      CoAuthorsAgreeToEthics: getFieldConfig(
        formFields,
        'CoAuthorsAgreeToEthics'
      ),
      CoAuthorManuscriptOriginalityConfirmed: getFieldConfig(
        formFields,
        'CoAuthorManuscriptOriginalityConfirmed'
      ),
      CoAuthorManuscriptNotUnderConsiderationElsewhere: getFieldConfig(
        formFields,
        'CoAuthorManuscriptNotUnderConsiderationElsewhere'
      ),
      CoAuthorAuthorshipAndConflictDeclaration: getFieldConfig(
        formFields,
        'CoAuthorAuthorshipAndConflictDeclaration'
      ),
      CoAuthorResearchEthicsComplianceConfirmed: getFieldConfig(
        formFields,
        'CoAuthorResearchEthicsComplianceConfirmed'
      ),
      CoAuthorExportControlComplianceConfirmed: getFieldConfig(
        formFields,
        'CoAuthorExportControlComplianceConfirmed'
      )
    };

    const preferenceStepConfigs: IPreferenceStepConfig = {
      CommentForEditor: getFieldConfig(formFields, 'CommentForEditor'),
      CoAuthorApproval: getFieldConfig(formFields, 'CoAuthorApproval'),
      PublishingModel: getFieldConfig(formFields, 'PublishingModel')!,
      OpenAccessChoice: getFieldConfig(formFields, 'OpenAccessChoice'),
      OpenAccessChoiceWorldBankACountry: getFieldConfig(
        formFields,
        'OpenAccessChoiceWorldBankACountry'
      ),
      OpenAccessChoiceWorldBankBCountry: getFieldConfig(
        formFields,
        'OpenAccessChoiceWorldBankBCountry'
      ),
      OpenAccessChoiceReviewerDiscountRate: getFieldConfig(
        formFields,
        'OpenAccessChoiceReviewerDiscountRate'
      ),
      DataAvailability: getFieldConfig(formFields, 'DataAvailability'),
      DataAvailabilityOpenlyAvailableUrl: getFieldConfig(
        formFields,
        'DataAvailabilityOpenlyAvailableUrl'
      ),
      DataAvailabilityEmbargoUrl: getFieldConfig(
        formFields,
        'DataAvailabilityEmbargoUrl'
      ),
      DataAvailabilityEmbargoDate: getFieldConfig(
        formFields,
        'DataAvailabilityEmbargoDate'
      ),
      DataUnavailabilityReason: getFieldConfig(
        formFields,
        'DataUnavailabilityReason'
      ),
      DataAvailabilityStatement: getFieldConfig(
        formFields,
        'DataAvailabilityStatement'
      ),
      PreferenceImmediatePublication: getFieldConfig(
        formFields,
        'PreferenceImmediatePublication'
      ),
      DoubleAnonymousPeerReview: getFieldConfig(
        formFields,
        'DoubleAnonymousPeerReview'
      ),
      AnonymizedManuscript: getFieldConfig(formFields, 'AnonymizedManuscript'),
      NoveltyStatement: getFieldConfig(formFields, 'NoveltyStatement'),
      FiguresAndTables: getFieldConfig(formFields, 'FiguresAndTables'),
      LicensesAndPermissions: getFieldConfig(
        formFields,
        'LicensesAndPermissions'
      ),
      LicensesAndPermissionsReproducedContent: getFieldConfig(
        formFields,
        'LicensesAndPermissionsReproducedContent'
      ),
      LicensesAndPermissionsFiles: getFieldConfig(
        formFields,
        'LicensesAndPermissionsFiles'
      ),

      RecommendedReviewers: getFieldConfig(formFields, 'RecommendedReviewers'),
      OpposedReviewers: getFieldConfig(formFields, 'OpposedReviewers'),
      TransparentPeerReview: getFieldConfig(
        formFields,
        'TransparentPeerReview'
      ),
      EthicsStatement: getFieldConfig(formFields, 'EthicsStatement'),
      ClinicalTrials: getFieldConfig(formFields, 'ClinicalTrials'),
      Funding: getFieldConfig(formFields, 'Funding'),
      FundingGeneric: getFieldConfig(formFields, 'FundingGeneric'),
      AcceleratedPublication: getFieldConfig(
        formFields,
        'AcceleratedPublication'
      ),
      IPEMMembership: getFieldConfig(formFields, 'IPEMMembership'),
      SubjectArea: getFieldConfig(formFields, 'SubjectArea'),
      PreviouslySubmitted: getFieldConfig(formFields, 'PreviouslySubmitted'),
      ConflictOfInterestForAuthor: getFieldConfig(
        formFields,
        'ConflictOfInterestForAuthor'
      ),
      AITools: getFieldConfig(formFields, 'AITools'),
      Preprint: getFieldConfig(formFields, 'Preprint'),
      SharedDataset: getFieldConfig(formFields, 'SharedDataset'),
      GeneratedDataset: getFieldConfig(formFields, 'GeneratedDataset'),
      UtilizedDataset: getFieldConfig(formFields, 'UtilizedDataset'),
      EthicalApproval: getFieldConfig(formFields, 'EthicalApproval'),
      EthicsApproval: getFieldConfig(formFields, 'EthicsApproval'),
      CopyrightLicensingForm: getFieldConfig(
        formFields,
        'CopyrightLicensingForm'
      ),
      ManuscriptOwnership: getFieldConfig(formFields, 'ManuscriptOwnership'),
      OpenAccessOnly: getFieldConfig(formFields, 'OpenAccessOnly'),
      BlindedReviewConsent: getFieldConfig(formFields, 'BlindedReviewConsent'),
      ApcAgreement: getFieldConfig(formFields, 'ApcAgreement'),
      OpenAccessPublishingConsent: getFieldConfig(
        formFields,
        'OpenAccessPublishingConsent'
      ),
      EthicsChecklist: getFieldConfig(formFields, 'EthicsChecklist'),
      OverlappingMaterial: getFieldConfig(formFields, 'OverlappingMaterial'),
      BlindedPeerReviewConsent: getFieldConfig(
        formFields,
        'BlindedPeerReviewConsent'
      ),
      AuthorDeclaration: getFieldConfig(formFields, 'AuthorDeclaration'),
      DataAvailabilityText: getFieldConfig(formFields, 'DataAvailabilityText')
    };

    const figuresAndTablesStepConfigs: IFiguresAndTablesStepConfig = {
      Figures: getFieldConfig(formFields, 'Figures'),
      Tables: getFieldConfig(formFields, 'Tables')
    };

    const updatedSteps: IStepConfig[] = [
      {
        stepKey: 'draft' as const,
        Component: DraftSubmissionStep,
        configs: null,
        displayName: null
      },
      {
        stepKey: 'upload' as const,
        Component: UploadStep,
        configs: null,
        displayName: null
      },
      {
        stepKey: 'metadataExtraction' as const,
        Component: MetadataExtractionStep,
        configs: metadataExtractionStepConfigs,
        displayName: null
      },
      ...(isNotEmpty(detailStepConfigs)
        ? [
            {
              stepKey: 'details' as const,
              Component: DetailsStep,
              configs: detailStepConfigs,
              displayName: detailsStepDisplayName
            }
          ]
        : []),

      // ...(isNotEmpty(figuresAndTablesStepConfigs)
      //   ? [
      //       {
      //         Component: FiguresAndTablesStep,
      //         configs: figuresAndTablesStepConfigs
      //       }
      //     ]
      //   : []),
      ...(isNotEmpty(authorStepConfigs)
        ? [
            {
              stepKey: 'author' as const,
              Component: AuthorStep,
              configs: authorStepConfigs,
              displayName: authorStepDisplayName
            }
          ]
        : []),
      ...(isNotEmpty(preferenceStepConfigs)
        ? [
            {
              stepKey: 'preference' as const,
              Component: PreferenceStep,
              configs: preferenceStepConfigs,
              displayName: preferenceStepDisplayName
            }
          ]
        : []),
      {
        stepKey: 'preview' as const,
        Component: PreviewStep,
        configs: {
          ArticleSubtype: getFieldConfig(formFields, 'ArticleSubtype'),
          ArticleLanguage: getFieldConfig(formFields, 'ArticleLanguage'),
          ...detailStepConfigs,
          ...authorStepConfigs,
          ...preferenceStepConfigs,
          ...figuresAndTablesStepConfigs
        },
        displayName: 'Preview Submission'
      },
      {
        stepKey: 'success' as const,
        Component: SuccessStep,
        configs: null,
        displayName: null
      }
    ];

    setSteps(updatedSteps);
  }, [
    formFields.length,
    detailsStepDisplayName,
    authorStepDisplayName,
    preferenceStepDisplayName
  ]);

  return {
    steps
  };
};
